// Which fields of each entity are replicated. Anything not listed here is
// device-local (e.g. the cached FSRS schedule on cards, derived from review logs).
// Keys listed in `nested` are merged per sub-key, addressed as "parent.child".

export const KINDS = {
  deck: {
    table: 'decks',
    keys: ['name', 'parentId', 'position', 'config', 'deletedAt', 'purged', 'createdAt'],
    nested: ['config'],
  },
  note: {
    table: 'notes',
    keys: ['deckId', 'type', 'fields', 'tags', 'deletedAt', 'purged', 'createdAt', 'updatedAt'],
    nested: ['fields'],
  },
  card: {
    table: 'cards',
    keys: ['noteId', 'deckId', 'ord', 'flag', 'suspended', 'buriedUntil', 'deletedAt', 'createdAt'],
    nested: [],
  },
  reviewLog: {
    table: 'reviewLogs',
    keys: ['cardId', 'rating', 'kind', 'reviewedAt', 'durationMs', 'deviceId', 'deletedAt'],
    nested: [],
  },
  // One row per account. `swipe` merges per direction so two devices can edit
  // different gestures without overwriting each other.
  settings: {
    table: 'settings',
    keys: [
      'theme',
      'accent',
      'textSize',
      'cardFont',
      'cardAlign',
      'motion',
      'ratingButtons',
      'swipe',
      'swipeEnabled',
      'showIntervals',
      'haptics',
      'autoCloudBackup',
      'sidebar',
      'sidebarDecks',
      'width',
      'density',
      'tabLabels',
      'homeToday',
      'afterAdd',
      'addPreview',
      'studyProgress',
      'studyChip',
    ],
    nested: ['swipe'],
  },
};

export const KIND_NAMES = Object.keys(KINDS);
export const SYNC_TABLES = KIND_NAMES.map((k) => KINDS[k].table);

export function tableOf(kind) {
  const def = KINDS[kind];
  if (!def) throw new Error(`Unknown kind: ${kind}`);
  return def.table;
}

function isPlainObject(v) {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

/**
 * Split a patch into replicated flat keys and local-only fields.
 * { fields: { front: x } } on a note -> synced { 'fields.front': x }
 */
export function splitPatch(kind, patch) {
  const def = KINDS[kind];
  const synced = {};
  const local = {};
  for (const [key, value] of Object.entries(patch)) {
    if (!def.keys.includes(key)) {
      if (key !== 'id' && key !== '_hlc') local[key] = value;
      continue;
    }
    if (def.nested.includes(key) && isPlainObject(value)) {
      for (const [sub, subValue] of Object.entries(value)) synced[`${key}.${sub}`] = subValue;
    } else {
      synced[key] = value;
    }
  }
  return { synced, local };
}

export function getFlat(row, flatKey) {
  const dot = flatKey.indexOf('.');
  if (dot === -1) return row[flatKey] ?? null;
  const parent = row[flatKey.slice(0, dot)];
  return parent?.[flatKey.slice(dot + 1)] ?? null;
}

/** Mutates `row`. */
export function setFlat(row, flatKey, value) {
  const dot = flatKey.indexOf('.');
  if (dot === -1) {
    row[flatKey] = value;
    return;
  }
  const parentKey = flatKey.slice(0, dot);
  const parent = isPlainObject(row[parentKey]) ? { ...row[parentKey] } : {};
  parent[flatKey.slice(dot + 1)] = value;
  row[parentKey] = parent;
}

/** Wire representation of selected keys of a row: { data, hlc }. */
export function rowToWire(row, keys = Object.keys(row._hlc ?? {})) {
  const data = {};
  const hlc = {};
  for (const k of keys) {
    if (!row._hlc?.[k]) continue;
    data[k] = getFlat(row, k);
    hlc[k] = row._hlc[k];
  }
  return { data, hlc };
}
